import React, { useEffect, useMemo, useState } from "react";
import "./VariantsSelector.css";
import { useTranslation } from "react-i18next";
import { LuPalette, LuRuler, LuCheck } from "react-icons/lu";


const VariantsSelector = ({
  variants,
  selectedVariant,
  setSelectedVariant,
}) => {
  const [selectedColor, setSelectedColor] = useState(selectedVariant?.color || "");
  const [selectedSize, setSelectedSize] = useState(selectedVariant?.size || "");
  const [_] = useTranslation("global");

  useEffect(() => {
    if (selectedVariant) {
      if (selectedVariant.color !== undefined) {
        setSelectedColor(selectedVariant.color);
      }
      if (selectedVariant.size !== undefined) {
        setSelectedSize(selectedVariant.size);
      }
    }
  }, [selectedVariant]);

  const availableColors = useMemo(() => {
    if (!variants || !Array.isArray(variants)) return [];
    const set = new Set();
    variants.forEach((v) => {
      if (v.color && v.color.trim() !== "") {
        set.add(v.color.trim());
      }
    });
    return Array.from(set);
  }, [variants]);

  const availableSizes = useMemo(() => {
    if (!variants || !Array.isArray(variants)) return [];
    const set = new Set();
    variants.forEach((v) => {
      const hasSize = v.size && v.size.trim() !== "";
      if (hasSize) {
        if (!availableColors.length || !selectedColor || v.color === selectedColor) {
          set.add(v.size.trim());
        }
      }
    });
    // Fallback if no sizes match current color
    if (set.size === 0) {
      variants.forEach((v) => {
        if (v.size && v.size.trim() !== "") set.add(v.size.trim());
      });
    }
    return Array.from(set);
  }, [variants, availableColors.length, selectedColor]);

  const handleColorClick = (color) => {
    setSelectedColor(color);
    const match =
      variants.find((v) => v.color === color && v.size === selectedSize) ||
      variants.find((v) => v.color === color) ||
      variants[0];
    if (match) {
      setSelectedVariant(match);
      if (match.size) setSelectedSize(match.size);
    }
  };

  const handleSizeClick = (size) => {
    setSelectedSize(size);
    const match =
      variants.find((v) => v.size === size && v.color === selectedColor) ||
      variants.find((v) => v.size === size) ||
      variants[0];
    if (match) {
      setSelectedVariant(match);
      if (match.color) setSelectedColor(match.color);
    }
  };

  if (!availableColors.length && !availableSizes.length) {
    return null;
  }

  return (
    <div className="variants-selector-wrapper">
      {/* Colors Section */}
      {availableColors.length > 0 && (
        <div className="variant-section">
          <div className="variant-section-header">
            <span className="variant-section-title">
              <LuPalette size={16} style={{ color: "#59c6c9" }} />
              <span>{_("panel.color") || "اللون"}:</span>
            </span>
            {(selectedVariant?.color || selectedColor) && (
              <span className="variant-selected-badge">
                {selectedVariant?.color || selectedColor}
              </span>
            )}
          </div>

          <div className="variant-pills-row">
            {availableColors.map((color) => {
              const isSelected =
                (selectedVariant?.color && selectedVariant.color === color) ||
                selectedColor === color;

              return (
                <button
                  type="button"
                  key={color}
                  className={`variant-pill color-pill ${isSelected ? "active" : ""}`}
                  onClick={() => handleColorClick(color)}
                >
                  <span>{color}</span>
                  {isSelected && <LuCheck className="variant-pill-check" size={13} />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sizes Section */}
      {availableSizes.length > 0 && (
        <div className="variant-section">
          <div className="variant-section-header">
            <span className="variant-section-title">
              <LuRuler size={16} style={{ color: "#59c6c9" }} />
              <span>{_("panel.size") || "المقاس"}:</span>
            </span>
            {(selectedVariant?.size || selectedSize) && (
              <span className="variant-selected-badge">
                {selectedVariant?.size || selectedSize}
              </span>
            )}
          </div>

          <div className="variant-pills-row">
            {availableSizes.map((size) => {
              const isSelected =
                (selectedVariant?.size && selectedVariant.size === size) ||
                selectedSize === size;

              return (
                <button
                  type="button"
                  key={size}
                  className={`variant-pill size-pill ${isSelected ? "active" : ""}`}
                  onClick={() => handleSizeClick(size)}
                >
                  <span>{size}</span>
                  {isSelected && <LuCheck className="variant-pill-check" size={13} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantsSelector;
